import React, { useState } from 'react';
import styled from 'styled-components';
import { FiX, FiUnlock, FiLock, FiSettings, FiChevronLeft, FiChevronRight, FiPieChart, FiHexagon } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useTokenization } from '../../contexts/TokenizationContext';
import toast from 'react-hot-toast';

const WizardContainer = styled.div`
  padding: 2rem;
  height: 100%;
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const WizardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const WizardTitle = styled.h2`
  font-size: 1.8rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #a0a0a0;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const StepContent = styled(motion.div)`
  min-height: 400px;
`;

const NFTSelector = styled.div`
  margin-bottom: 2rem;

  h3 {
    color: #fff;
    margin-bottom: 1rem;
  }
`;

const NFTGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const NFTCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid ${props => props.selected ? '#4facfe' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #4facfe;
    transform: translateY(-2px);
  }

  .nft-header {
    display: flex;
    justify-content: space-between;
    align-items: start;
    margin-bottom: 1rem;
  }

  .nft-icon {
    font-size: 2rem;
    color: #4facfe;
  }

  .nft-value {
    color: #00ff88;
    font-weight: 600;
    font-size: 1.1rem;
  }

  .nft-name {
    font-size: 1.2rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 0.5rem;
  }

  .nft-details {
    color: #a0a0a0;
    font-size: 0.9rem;
    line-height: 1.4;
    margin-bottom: 1rem;
  }

  .nft-contract {
    font-family: monospace;
    background: rgba(79, 172, 254, 0.1);
    border: 1px solid rgba(79, 172, 254, 0.3);
    border-radius: 6px;
    padding: 0.5rem;
    font-size: 0.8rem;
    color: #4facfe;
  }
`;

const ConfigurationForm = styled.div`
  h3 {
    color: #fff;
    margin-bottom: 1.5rem;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const FormSection = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;

  .section-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  label {
    display: block;
    font-weight: 500;
    color: #fff;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
  }

  input, select {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
    font-size: 0.9rem;
    transition: all 0.3s ease;

    &:focus {
      border-color: #4facfe;
      box-shadow: 0 0 0 2px rgba(79, 172, 254, 0.1);
    }

    &::placeholder {
      color: #a0a0a0;
    }
  }

  select {
    option {
      background: #1a1a3e;
      color: #fff;
    }
  }

  .error {
    color: #ff4757;
    font-size: 0.8rem;
    margin-top: 0.25rem;
  }

  .helper {
    color: #a0a0a0;
    font-size: 0.8rem;
    margin-top: 0.25rem;
  }
`;

const FractionPreview = styled.div`
  background: rgba(79, 172, 254, 0.05);
  border: 1px solid rgba(79, 172, 254, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 1rem;

  .preview-title {
    font-weight: 600;
    color: #4facfe;
    margin-bottom: 1rem;
  }

  .preview-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
  }

  .preview-item {
    text-align: center;

    .value {
      font-size: 1.5rem;
      font-weight: 700;
      color: #fff;
      margin-bottom: 0.25rem;
    }

    .label {
      color: #a0a0a0;
      font-size: 0.9rem;
    }
  }
`;

const ReviewSection = styled.div`
  h3 {
    color: #fff;
    margin-bottom: 1.5rem;
  }
`;

const ReviewCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;

  .review-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .review-icon {
    font-size: 2rem;
    color: #4facfe;
  }

  .review-title {
    font-size: 1.4rem;
    font-weight: 600;
    color: #fff;
  }

  .review-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }

  .review-item {
    .label {
      color: #a0a0a0;
      font-size: 0.9rem;
      margin-bottom: 0.25rem;
    }

    .value {
      color: #fff;
      font-weight: 600;
    }
  }
`;

const VaultVisualization = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  margin-bottom: 2rem;

  .vault-icon {
    font-size: 4rem;
    color: #4facfe;
    margin-bottom: 1rem;
  }

  .vault-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 0.5rem;
  }

  .vault-description {
    color: #a0a0a0;
    line-height: 1.5;
  }

  .vault-process {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    margin-top: 2rem;
    flex-wrap: wrap;
  }

  .process-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem;
    background: rgba(79, 172, 254, 0.1);
    border-radius: 8px;
    min-width: 120px;

    .step-icon {
      font-size: 1.5rem;
      color: #4facfe;
    }

    .step-label {
      font-size: 0.8rem;
      color: #a0a0a0;
      text-align: center;
    }
  }

  .process-arrow {
    color: #4facfe;
    font-size: 1.5rem;
  }
`;

const WizardActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &.primary {
    background: #4facfe;
    color: #fff;

    &:hover {
      background: #3d8bfe;
      transform: translateY(-1px);
    }

    &:disabled {
      background: #2a2a3e;
      color: #a0a0a0;
      cursor: not-allowed;
      transform: none;
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

function FractionalNFTWizard({ onClose }) {
  const { tokenizations, createFractionalNFT, loading } = useTokenization();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedNFT, setSelectedNFT] = useState(null);
  const [configuration, setConfiguration] = useState({
    totalFractions: 100,
    fractionName: '',
    fractionSymbol: '',
    redemptionThreshold: 0.9,
    custodian: ''
  });
  const [errors, setErrors] = useState({});

  // Filter NFT tokenizations
  const availableNFTs = tokenizations.filter(tokenization => 
    tokenization.tokenType === 'nonFungible' && tokenization.isActive
  );

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!selectedNFT) newErrors.nft = 'Please select an NFT to fractionalize';
        break;
      case 2:
        if (!configuration.fractionName) newErrors.fractionName = 'Fraction token name is required';
        if (!configuration.fractionSymbol) newErrors.fractionSymbol = 'Fraction token symbol is required';
        if (!configuration.totalFractions || configuration.totalFractions < 2) {
          newErrors.totalFractions = 'Must have at least 2 fractions';
        }
        if (configuration.redemptionThreshold < 0.5 || configuration.redemptionThreshold > 1) {
          newErrors.redemptionThreshold = 'Redemption threshold must be between 50% and 100%';
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;
    
    try {
      await createFractionalNFT(selectedNFT.id, configuration);
      toast.success('NFT fractionalized successfully!');
      onClose();
    } catch (error) {
      toast.error('Failed to fractionalize NFT');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <NFTSelector>
              <h3>Select NFT to Fractionalize</h3>
              <NFTGrid>
                {availableNFTs.map(nft => (
                  <NFTCard
                    key={nft.id}
                    selected={selectedNFT?.id === nft.id}
                    onClick={() => setSelectedNFT(nft)}
                  >
                    <div className="nft-header">
                      <FiHexagon className="nft-icon" />
                      <div className="nft-value">
                        ${nft.metadata?.valuationUSD?.toLocaleString() || 'N/A'}
                      </div>
                    </div>
                    <div className="nft-name">{nft.name}</div>
                    <div className="nft-details">
                      Symbol: {nft.symbol} | Standard: {nft.tokenType}
                    </div>
                    <div className="nft-contract">
                      {nft.contractAddress}
                    </div>
                  </NFTCard>
                ))}
              </NFTGrid>
              {availableNFTs.length === 0 && (
                <div style={{ color: '#a0a0a0', textAlign: 'center', padding: '2rem' }}>
                  No NFTs available for fractionalization.
                  <br />
                  Please tokenize assets as NFTs first.
                </div>
              )}
              {errors.nft && <div style={{ color: '#ff4757' }}>{errors.nft}</div>}
            </NFTSelector>
          </StepContent>
        );

      case 2:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <ConfigurationForm>
              <h3>Fractionalization Configuration</h3>
              <FormGrid>
                <FormSection>
                  <div className="section-title">
                    <FiPieChart />
                    Fraction Settings
                  </div>
                  <FormGroup>
                    <label>Total Fractions *</label>
                    <input
                      type="number"
                      placeholder="e.g., 100"
                      value={configuration.totalFractions}
                      onChange={(e) => setConfiguration(prev => ({ 
                        ...prev, 
                        totalFractions: Number(e.target.value) 
                      }))}
                    />
                    {errors.totalFractions && <div className="error">{errors.totalFractions}</div>}
                    <div className="helper">Number of shares to split the NFT into</div>
                  </FormGroup>
                  <FormGroup>
                    <label>Fraction Token Name *</label>
                    <input
                      type="text"
                      placeholder="e.g., Fractional Gold Certificate"
                      value={configuration.fractionName}
                      onChange={(e) => setConfiguration(prev => ({ 
                        ...prev, 
                        fractionName: e.target.value 
                      }))}
                    />
                    {errors.fractionName && <div className="error">{errors.fractionName}</div>}
                  </FormGroup>
                  <FormGroup>
                    <label>Fraction Token Symbol *</label>
                    <input
                      type="text"
                      placeholder="e.g., fGOLD"
                      value={configuration.fractionSymbol}
                      onChange={(e) => setConfiguration(prev => ({ 
                        ...prev, 
                        fractionSymbol: e.target.value.toUpperCase() 
                      }))}
                    />
                    {errors.fractionSymbol && <div className="error">{errors.fractionSymbol}</div>}
                  </FormGroup>
                </FormSection>

                <FormSection>
                  <div className="section-title">
                    <FiSettings />
                    Advanced Settings
                  </div>
                  <FormGroup>
                    <label>Redemption Threshold</label>
                    <select
                      value={configuration.redemptionThreshold}
                      onChange={(e) => setConfiguration(prev => ({ 
                        ...prev, 
                        redemptionThreshold: Number(e.target.value) 
                      }))}
                    >
                      <option value={0.5}>50% - Majority</option>
                      <option value={0.75}>75% - Supermajority</option>
                      <option value={0.9}>90% - Near consensus</option>
                      <option value={1.0}>100% - Full consensus</option>
                    </select>
                    <div className="helper">
                      Percentage needed to trigger NFT redemption
                    </div>
                  </FormGroup>
                  <FormGroup>
                    <label>Custodian</label>
                    <input
                      type="text"
                      placeholder="Vault manager or custodian"
                      value={configuration.custodian}
                      onChange={(e) => setConfiguration(prev => ({ 
                        ...prev, 
                        custodian: e.target.value 
                      }))}
                    />
                    <div className="helper">Entity managing the smart vault</div>
                  </FormGroup>
                </FormSection>
              </FormGrid>

              <FractionPreview>
                <div className="preview-title">Fractionalization Preview</div>
                <div className="preview-grid">
                  <div className="preview-item">
                    <div className="value">{configuration.totalFractions}</div>
                    <div className="label">Total Shares</div>
                  </div>
                  <div className="preview-item">
                    <div className="value">
                      {selectedNFT ? 
                        `$${((selectedNFT.metadata?.valuationUSD || 0) / configuration.totalFractions).toFixed(2)}` : 
                        '$0.00'
                      }
                    </div>
                    <div className="label">Price per Share</div>
                  </div>
                  <div className="preview-item">
                    <div className="value">{(configuration.redemptionThreshold * 100).toFixed(0)}%</div>
                    <div className="label">Redemption Trigger</div>
                  </div>
                </div>
              </FractionPreview>
            </ConfigurationForm>
          </StepContent>
        );

      case 3:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <ReviewSection>
              <h3>Review & Deploy Fractionalization</h3>
              
              <VaultVisualization>
                <FiLock className="vault-icon" />
                <div className="vault-title">Core Vault Process</div>
                <div className="vault-description">
                  Your NFT will be securely locked in a smart contract vault and fractional 
                  shares will be minted. Holders can trade fractions or collectively redeem the original NFT.
                </div>
                <div className="vault-process">
                  <div className="process-step">
                    <FiHexagon className="step-icon" />
                    <div className="step-label">NFT Locked</div>
                  </div>
                  <div className="process-arrow">→</div>
                  <div className="process-step">
                    <FiPieChart className="step-icon" />
                    <div className="step-label">Fractions Minted</div>
                  </div>
                  <div className="process-arrow">→</div>
                  <div className="process-step">
                    <FiUnlock className="step-icon" />
                    <div className="step-label">Trading Enabled</div>
                  </div>
                </div>
              </VaultVisualization>

              <ReviewCard>
                <div className="review-header">
                  <FiPieChart className="review-icon" />
                  <div className="review-title">Fractionalization Summary</div>
                </div>
                <div className="review-grid">
                  <div className="review-item">
                    <div className="label">Original NFT</div>
                    <div className="value">{selectedNFT?.name}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">NFT Value</div>
                    <div className="value">
                      ${selectedNFT?.metadata?.valuationUSD?.toLocaleString() || 'N/A'}
                    </div>
                  </div>
                  <div className="review-item">
                    <div className="label">Total Fractions</div>
                    <div className="value">{configuration.totalFractions.toLocaleString()}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Fraction Name</div>
                    <div className="value">{configuration.fractionName}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Fraction Symbol</div>
                    <div className="value">{configuration.fractionSymbol}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Price per Fraction</div>
                    <div className="value">
                      ${selectedNFT ? 
                        ((selectedNFT.metadata?.valuationUSD || 0) / configuration.totalFractions).toFixed(2) : 
                        '0.00'
                      }
                    </div>
                  </div>
                  <div className="review-item">
                    <div className="label">Redemption Threshold</div>
                    <div className="value">{(configuration.redemptionThreshold * 100).toFixed(0)}%</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Custodian</div>
                    <div className="value">{configuration.custodian || 'Core Contract'}</div>
                  </div>
                </div>
              </ReviewCard>

              <div style={{ 
                background: 'rgba(255, 193, 7, 0.1)', 
                border: '1px solid rgba(255, 193, 7, 0.3)',
                borderRadius: '12px',
                padding: '1.5rem',
                color: '#ffc107'
              }}>
                <strong>⚠️ Important Notice</strong>
                <p style={{ margin: '0.5rem 0 0 0', color: '#a0a0a0' }}>
                  Once fractionalized, the original NFT will be locked and cannot be accessed until 
                  the redemption threshold is met. This action cannot be reversed.
                </p>
              </div>
            </ReviewSection>
          </StepContent>
        );

      default:
        return null;
    }
  };

  return (
    <WizardContainer>
      <WizardHeader>
        <WizardTitle>
          <FiUnlock /> Fractionalize NFT
        </WizardTitle>
        <CloseButton onClick={onClose}>
          <FiX />
        </CloseButton>
      </WizardHeader>

      <AnimatePresence mode="wait">
        {renderStepContent()}
      </AnimatePresence>

      <WizardActions>
        <ActionButton 
          className="secondary" 
          onClick={handlePrevious}
          disabled={currentStep === 1}
        >
          <FiChevronLeft />
          Previous
        </ActionButton>

        {currentStep < 3 ? (
          <ActionButton className="primary" onClick={handleNext}>
            Next
            <FiChevronRight />
          </ActionButton>
        ) : (
          <ActionButton 
            className="primary" 
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Creating Fractions...' : 'Fractionalize NFT'}
            <FiLock />
          </ActionButton>
        )}
      </WizardActions>
    </WizardContainer>
  );
}

export default FractionalNFTWizard; 